import helpers from './helpers';
import { DEFAULT_COLOR_SCHEMES, CUSTOM_SCHEME_ID_RE } from './constants/common';

const {
  buildTemplateContext,
  buildColorSchemesRegistry,
  buildRegistrySchemeEntry,
  normalizeVariableDefinitions,
  buildOutput,
  resolveSchemeVariables,
  resolveSchemeVariable,
  parseColorInput,
  parseColorMap,
  normalizeSchemes,
  serializeColors,
  serializePaletteSchemes,
  extractRawState,
} = helpers;

const patchSchemeInOutput = (output, schemeId, schemeVars) => ({
  ...output,
  variables: {
    ...output.variables,
    schemes: { ...output.variables.schemes, [schemeId]: schemeVars },
  },
});

const deleteSchemeFromOutput = (output, schemeId) => {
  const { [schemeId]: _, ...remainingSchemes } = output.variables.schemes;
  return {
    ...output,
    variables: {
      ...output.variables,
      schemes: remainingSchemes,
    },
  };
};

// Runtime color palette engine.
// Initializes mutable engine state once, then supports partial updates at runtime.
export const createColorPaletteEngine = ({
  config,
  colors: inputColors,
  schemes: inputSchemes = {},
}) => {
  const initialContext = buildTemplateContext({ config, colors: parseColorMap(inputColors) });
  const initialVariableDefinitions = normalizeVariableDefinitions(config.variable_definitions);

  let engineState = {
    context: initialContext,
    colorSchemes: buildColorSchemesRegistry({
      context: initialContext,
      rulesets: config.rulesets,
      schemes: normalizeSchemes(inputSchemes, initialVariableDefinitions),
    }),
    variableDefinitions: initialVariableDefinitions,
    cachedOutput: null,
  };

  const getOrBuildOutput = () => {
    if (!engineState.cachedOutput) {
      engineState.cachedOutput = buildOutput(config, engineState);
    }
    return engineState.cachedOutput;
  };

  const exportState = () => {
    const { colors, schemes } = extractRawState(
      engineState.colorSchemes,
      engineState.context.colors
    );
    return {
      colors: serializeColors(colors),
      ...(Object.keys(schemes).length && { schemes: serializePaletteSchemes(schemes) }),
    };
  };

  const updateColors = (colorsToUpdate) => {
    const currentState = extractRawState(engineState.colorSchemes, engineState.context.colors);
    const context = buildTemplateContext({
      config,
      colors: { ...currentState.colors, ...parseColorMap(colorsToUpdate) },
    });

    engineState = {
      ...engineState,
      context,
      colorSchemes: buildColorSchemesRegistry({
        context,
        rulesets: config.rulesets,
        schemes: currentState.schemes,
      }),
      cachedOutput: null,
    };

    return getOrBuildOutput();
  };

  const updateSchemeOverrides = (schemeId, patchUpdate) => {
    const existingScheme = engineState.colorSchemes[schemeId];
    if (!existingScheme) {
      throw new Error(`Color scheme "${schemeId}" does not exist.`);
    }

    const output = getOrBuildOutput();
    const schemeVars = { ...output.variables.schemes[schemeId] };

    Object.entries(patchUpdate).forEach(([varName, rawValue]) => {
      const definition = engineState.variableDefinitions[varName];
      if (!definition) {
        throw new Error(`Variable "${varName}" is not defined.`);
      }

      const overrideValue = definition.outputModes.includes('string')
        ? rawValue
        : parseColorInput(rawValue);

      existingScheme.variableOverrides[varName] = overrideValue;

      Object.assign(
        schemeVars,
        resolveSchemeVariable({
          varName,
          definition,
          scheme: existingScheme,
          assignVariable: engineState.context.assignVariable,
        })
      );
    });

    engineState.cachedOutput = patchSchemeInOutput(output, schemeId, schemeVars);
    return engineState.cachedOutput;
  };

  const resetSchemeOverrides = (schemeId, variablesToReset) => {
    const existingScheme = engineState.colorSchemes[schemeId];
    if (!existingScheme) {
      throw new Error(`Color scheme "${schemeId}" does not exist.`);
    }

    const output = getOrBuildOutput();
    const schemeVars = { ...output.variables.schemes[schemeId] };

    variablesToReset.forEach((varName) => {
      const definition = engineState.variableDefinitions[varName];
      if (!definition) {
        throw new Error(`Variable "${varName}" is not defined.`);
      }
      delete existingScheme.variableOverrides[varName];

      Object.assign(
        schemeVars,
        resolveSchemeVariable({
          varName,
          definition,
          scheme: existingScheme,
          assignVariable: engineState.context.assignVariable,
        })
      );
    });

    engineState.cachedOutput = patchSchemeInOutput(output, schemeId, schemeVars);
    return engineState.cachedOutput;
  };

  const setCustomScheme = (schemeId, { baseColor, ...schemeData }) => {
    if (schemeId in DEFAULT_COLOR_SCHEMES) {
      throw new Error(`Scheme "${schemeId}" is not a custom scheme`);
    }

    if (!CUSTOM_SCHEME_ID_RE.test(schemeId)) {
      throw new Error(
        `Invalid custom scheme ID "${schemeId}". Expected format: ws-custom-{colorType}-{id}`
      );
    }

    const { context, colorSchemes, variableDefinitions } = engineState;

    const schemeEntry = buildRegistrySchemeEntry({
      schemeId,
      schemeData: { ...schemeData, baseColor: parseColorInput(baseColor) },
      context,
      rulesets: config.rulesets,
    });
    colorSchemes[schemeId] = schemeEntry;

    const output = getOrBuildOutput();
    const schemeVars = resolveSchemeVariables({
      context,
      variableDefinitions,
      scheme: schemeEntry,
    });

    engineState.cachedOutput = patchSchemeInOutput(output, schemeId, schemeVars);
    return engineState.cachedOutput;
  };

  const deleteCustomScheme = (schemeId) => {
    if (schemeId in DEFAULT_COLOR_SCHEMES) {
      throw new Error(`Scheme "${schemeId}" is not a custom scheme`);
    }

    const { colorSchemes } = engineState;
    if (!(schemeId in colorSchemes)) {
      throw new Error(`Scheme "${schemeId}" does not exist.`);
    }

    delete colorSchemes[schemeId];

    engineState.cachedOutput = deleteSchemeFromOutput(getOrBuildOutput(), schemeId);
    return engineState.cachedOutput;
  };

  const api = {
    exportState,
    getOutput: () => getOrBuildOutput(),
    updateColors,
    updateSchemeOverrides,
    resetSchemeOverrides,
    setCustomScheme,
    deleteCustomScheme,
  };

  return api;
};

// Generates color palettes once at build time.
// Runtime palette updates are not supported.
// Used to generate default color palettes for ws-templates.
const generateColorPalette = ({ config, colors }) => {
  const engine = createColorPaletteEngine({ config, colors });
  const result = engine.getOutput();

  const mergedDefaultSchemeVariables = Object.entries(result.variables.schemes).reduce(
    (acc, [id, vars]) => {
      if (id in DEFAULT_COLOR_SCHEMES) {
        const varPrefix = `${id.replace(/^ws-/, 'ws_surface_').replace(/-/g, '_')}_`;
        Object.entries(vars).forEach(([key, value]) => {
          acc[`${varPrefix}${key}`] = value;
        });
      }
      return acc;
    },
    {}
  );
  return {
    colors: result.colors,
    variables: {
      ...result.variables.root,
      ...mergedDefaultSchemeVariables,
    },
  };
};

export default generateColorPalette;
